import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchDealers, type Dealer } from './api.js';
import { dealerCities, dealerMapUrl, dealerPhoneHref, dealerSiteUrl, filterDealers } from './dealers.js';
import type { MaxWebApp } from './max.js';
import { DealerCityPicker } from './DealerCityPicker.js';
import { noActivity, type TrackActivity } from './activity.js';

const PAGE_SIZE = 10;

export function FindDealerScreen({ webApp, track = noActivity, title = 'Найти дилера' }: {
  webApp?: MaxWebApp; track?: TrackActivity; title?: string;
}) {
  const [items, setItems] = useState<Dealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [cityId, setCityId] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchDealers().then((response) => {
      if (!cancelled) setItems(response.items);
    }).catch((reason: unknown) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : 'Не удалось загрузить список дилеров.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attempt]);
  const cities = useMemo(() => dealerCities(items), [items]);
  const results = useMemo(() => filterDealers(items, cityId), [items, cityId]);
  const searching = cityId !== '';
  const openExternal = (event: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    if (webApp?.initData && webApp.openLink) {
      event.preventDefault();
      webApp.openLink(url);
    }
  };

  return (
    <section className="station-search dealer-search" aria-labelledby="dealer-title">
      <h1 className="station-search__title" id="dealer-title">{title}</h1>
      <p className="station-search__subtitle">Выберите город или регион, чтобы найти контакты дилеров ТОНАР</p>
      <DealerCityPicker cities={cities} value={cityId} disabled={loading || !!error || cities.length === 0}
        onChange={(id) => { if (id !== cityId) track('find_dealer', 'region_select', id); setCityId(id); setVisibleCount(PAGE_SIZE); }} />
      {cityId && !loading ? <button className="dealer-search__reset" type="button" onClick={() => {
        setCityId(''); setVisibleCount(PAGE_SIZE);
      }}>Сбросить выбор</button> : null}
      {loading ? <p className="station-search__message" role="status">Загружаем список дилеров…</p> : null}
      {error ? <div role="alert"><p className="station-search__message station-search__message--error">{error}</p>
        <button className="station-results__more" type="button" onClick={() => setAttempt((value) => value + 1)}>Повторить загрузку</button>
      </div> : null}
      {!loading && !error && items.length === 0 ? <p className="station-search__message">Список дилеров пока не загружен.</p> : null}
      {!loading && !error && items.length > 0 && !searching ? <p className="station-search__message">
        Выберите город или регион из списка. В справочнике: {items.length}.
      </p> : null}
      {!loading && !error && items.length > 0 && searching ? <section className="station-results" aria-live="polite">
        <div className="station-results__heading"><h2>Дилеры</h2><span>{results.length}</span></div>
        {!results.length ? <p className="station-search__message">В этом регионе пока нет дилеров.</p> : null}
        <ul className="station-results__list">
          {results.slice(0, visibleCount).map((dealer) => <DealerCard key={dealer.id} dealer={dealer} openExternal={openExternal} track={track} />)}
        </ul>
        {visibleCount < results.length ? <button className="station-results__more" type="button"
          onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>Показать ещё</button> : null}
      </section> : null}
    </section>
  );
}

function DealerCard({ dealer, openExternal, track }: {
  dealer: Dealer;
  openExternal: (event: React.MouseEvent<HTMLAnchorElement>, url: string) => void;
  track: TrackActivity;
}) {
  const [phonesOpen, setPhonesOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const phones = dealer.phones.map((label) => ({ label, href: dealerPhoneHref(label) })).filter((p) => p.href !== null);
  const sites = dealer.sites.map(dealerSiteUrl).filter((url): url is string => url !== null);
  const mapUrl = dealerMapUrl(dealer);
  useEffect(() => {
    if (!phonesOpen) return;
    dialogRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPhonesOpen(false);
      if (event.key !== 'Tab') return;
      const elements = dialogRef.current?.querySelectorAll<HTMLElement>('a, button');
      if (!elements?.length) return;
      const first = elements[0]!;
      const last = elements[elements.length - 1]!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); buttonRef.current?.focus(); };
  }, [phonesOpen]);
  return <li className="station-card dealer-card">
    <div className="station-card__topline"><h3>{dealer.organization}</h3></div>
    <p className="dealer-card__region">{dealer.region}</p>
    <p className="station-card__address">{dealer.address || 'Адрес не указан — уточните у дилера'}</p>
    <div className="station-card__actions">
      {phones.length === 1 ? <a href={phones[0]!.href!} onClick={() => track('find_dealer', 'phone_click', dealer.id)}>Позвонить</a> : null}
      {phones.length > 1 ? <button ref={buttonRef} type="button" onClick={() => { track('find_dealer', 'phone_picker', dealer.id); setPhonesOpen(true); }}>Позвонить</button> : null}
      {mapUrl ? <a href={mapUrl} onClick={(event) => { track('find_dealer', 'map_click', dealer.id); openExternal(event, mapUrl); }}>На карте</a> : null}
      {sites.map((site, index) => <a key={site} href={site} onClick={(event) => { track('find_dealer', 'site_click', dealer.id); openExternal(event, site); }}>
        {sites.length === 1 ? 'Сайт' : `Сайт ${index + 1}`}
      </a>)}
      {dealer.emails.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)).map((email) =>
        <a key={email} href={`mailto:${encodeURIComponent(email)}`} onClick={() => track('find_dealer', 'email_click', dealer.id)}>{email}</a>)}
    </div>
    {!phones.length && !sites.length && !dealer.emails.length ? <p className="station-results__note">Контакты не указаны в источнике.</p> : null}
    {phonesOpen ? <div className="modal-overlay" onClick={() => setPhonesOpen(false)}>
      <div ref={dialogRef} className="modal station-phone-picker" role="dialog" aria-modal="true"
        aria-labelledby={`dealer-phones-${dealer.id}`} onClick={(event) => event.stopPropagation()}>
        <h2 className="modal__title" id={`dealer-phones-${dealer.id}`}>Выберите номер</h2>
        <p className="station-results__note">{dealer.organization}</p>
        <div className="station-phone-picker__list">{phones.map((phone) =>
          <a className="station-phone-picker__number" key={phone.label} href={phone.href!} onClick={() => track('find_dealer', 'phone_click', dealer.id)}>{phone.label}</a>)}</div>
        <div className="modal__actions"><button className="modal__cancel" type="button" onClick={() => setPhonesOpen(false)}>Отмена</button></div>
      </div>
    </div> : null}
  </li>;
}
